// Wire types for /api/search-indexes.
//
// The deployment keeps one set of concrete, version-suffixed indices per
// logical stream. An upgrade that ships a new version hands the writes and the
// reads to it by itself and retires the old index, which keeps its records and
// stays readable. Copying that older history across is offered, not required.

// Two states, not three. An earlier revision had "idle" for a created-but-
// not-yet-in-use index; reconciliation now creates the build's version and
// hands it the writes in one step, so nothing can be in that state.
export type IndexState = "active" | "frozen";

export type MigrationState = "running" | "succeeded" | "failed" | "cancelled";

export interface SearchIndexEntry {
    name: string
    version: number
    state: IndexState
    // enabledAt is when the index started taking the writes. A migration copies
    // everything older than this, because everything newer is already there.
    enabledAt?: number
    disabledAt?: number
    // lastWriteAt is what makes a retired index legible as retired rather
    // than looking like a second live copy.
    lastWriteAt?: number
    // current marks the version this build targets.
    current: boolean
    docs: number
    // docsKnown is false when the cluster gave no count — it could not be
    // listed, or could not read the index (closed, or shards unassigned). An
    // unknown count must not render as "empty", and deleting such an index
    // needs the typed confirmation because the server refuses it otherwise.
    docsKnown: boolean
    sizeBytes: number
}

export interface SearchIndexMigration {
    // id identifies one copy for the life of the record. Absent on records
    // written before the field existed.
    id?: string
    from: string
    to: string
    taskId?: string
    state: MigrationState
    startedAt: number
    finishedAt?: number
    cutoverMs: number
    total: number
    created: number
    updated: number
    deleted: number
    // versionConflicts are documents the live write path already placed in
    // the destination, so they count as handled rather than as errors.
    versionConflicts: number
    batches: number
    throttleRps: number
    throttledMillis: number
    failures?: string[]
    error?: string
}

export interface SearchIndexStream {
    stream: string
    current: string
    readIndex: string
    // available is set when the version this build targets does not exist.
    // Startup creates and uses it automatically, so this means the create did
    // not happen — usually a cluster that refused it — and the UI offers a
    // retry rather than a first step.
    available?: string
    indices: SearchIndexEntry[]
    // migration is the running copy, or the most recent one.
    migration?: SearchIndexMigration
    migrations?: SearchIndexMigration[]
    // readSubstrFields is the read target's capability level. When it is
    // shorter than fullSubstrFields, substring filtering against that index is
    // degraded and the UI has to say so.
    readSubstrFields: string[]
    fullSubstrFields: string[]
}

export interface GetSearchIndexesResponse {
    streams: SearchIndexStream[]
    fetchedAt: number
}

export const STREAM_LABEL: Record<string, string> = {
    activities: "Activity log",
    alert_log: "Alert log",
};

export const INDEX_STATE_LABEL: Record<IndexState, string> = {
    active: "Active",
    frozen: "Frozen",
};

// handled counts the documents a copy has accounted for.
export function migrationHandled(m: SearchIndexMigration): number {
    return m.created + m.updated + m.deleted + m.versionConflicts;
}

// migrationPercent is progress as a whole number, or 0 while the total is
// still unknown.
export function migrationPercent(m: SearchIndexMigration): number {
    if (m.total <= 0) return 0;
    return Math.min(100, Math.round((migrationHandled(m) / m.total) * 100));
}

// --- Migration preview ---------------------------------------------------
//
// What a copy would do, field by field, so an operator can decide whether to
// run one. The interesting cases are not the fields that match.

export type MigrationFieldStatus =
    // Both mappings agree; copied unchanged.
    | "carried"
    // The new mapping adds subfields, so filters that do not work on the old
    // index will work on the copied records.
    | "enriched"
    // New in this version: no old record carries it, so it stays empty.
    | "added"
    // Only the old mapping declares it. Values are still copied, and mapped
    // as they arrive.
    | "undeclared"
    // Mapped as two different types — the only case that can fail.
    | "conflict";

export interface MigrationFieldPlan {
    field: string
    sourceType?: string
    targetType?: string
    status: MigrationFieldStatus
    // gained lists the subfields the destination adds, for an improved field.
    gained?: string[]
}

export interface MigrationPreview {
    from: string
    to: string
    sourceDocs: number
    sourceSizeBytes: number
    sourceStoppedAt?: number
    targetDocs: number
    targetEnabledAt?: number
    cutoverMs: number
    // wholeIndex is set when every record in the source is in scope, which is
    // the usual case for a retired index.
    wholeIndex: boolean
    fields: MigrationFieldPlan[]
    carried: number
    enriched: number
    added: number
    undeclared: number
    conflicts: number
    blocking: boolean
    // mappingUnavailable means a mapping could not be read, so the comparison
    // is unknown rather than empty.
    mappingUnavailable: boolean
}

export const FIELD_STATUS_LABEL: Record<MigrationFieldStatus, string> = {
    conflict: "Needs attention",
    added: "Stays empty",
    enriched: "Improved",
    undeclared: "Copied as-is",
    carried: "Unchanged",
};

// recordsPhrase describes how many records an index holds, for the banners
// that name where the older log is. An index the cluster would not count gets
// a hedge rather than "0", which is the one number it must not claim.
export function recordsPhrase(entry: SearchIndexEntry): string {
    if (!entry.docsKnown) return "Older records may be";
    return `${entry.docs.toLocaleString("en-US")} older records are`;
}

// copiedInto names every index whose records have reached the named one,
// following chains: a copy only ever lands in the index receiving writes, so
// v8 -> v9 followed by v9 -> v10 leaves v8's records in v10 even though no
// copy names that pair.
//
// A chain link only counts when it finished before the next one started —
// otherwise the onward copy left before those records arrived, and they are
// genuinely still missing.
function copiedInto(migrations: SearchIndexMigration[], target: string): Set<string> {
    const succeeded = migrations.filter((m) => m.state === "succeeded");
    const copied = new Set<string>();

    // by is the moment the records had to already be in this index to have
    // been carried onward from it. Nothing constrains the target itself.
    const pending: { name: string, by: number }[] = [{ name: target, by: Infinity }];
    // Walking back through a chain can revisit an index under a later
    // deadline; a stricter one adds nothing, and this ends the walk on the
    // cycle two indices copied into each other would otherwise form.
    const walked = new Map<string, number>();

    while (pending.length > 0) {
        const step = pending.pop() as { name: string, by: number };
        const seen = walked.get(step.name);
        if (seen !== undefined && seen >= step.by) continue;
        walked.set(step.name, step.by);

        for (const m of succeeded) {
            if (m.to !== step.name) continue;
            if ((m.finishedAt ?? m.startedAt) > step.by) continue;
            copied.add(m.from);
            pending.push({ name: m.from, by: m.startedAt });
        }
    }
    return copied;
}

// missingFrom lists the indices that may hold records the named index does
// not: they are not known to be empty, and no finished copy has moved their
// records across. It reads the recorded copies rather than any per-index
// status, so it cannot disagree with what actually ran.
//
// An index whose count the cluster would not give up counts as possibly
// holding records. Treating unknown as empty would drop the index most likely
// to be the only copy of the older log out of both the warning and the list of
// things that can be copied across.
//
// stream.recordsElsewhere answers the same question server-side, but only for
// the read target and only for copies naming it directly, so it is not used
// here.
export function missingFrom(stream: SearchIndexStream, target: string): SearchIndexEntry[] {
    const copied = copiedInto(stream.migrations || [], target);

    return stream.indices
        .filter((i) => i.name !== target && !copied.has(i.name))
        .filter((i) => i.docs > 0 || !i.docsKnown)
        .sort((a, b) => b.docs - a.docs);
}
